import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import t from '../theme';

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

const FEES = {
  yes: { bg: t.greenLight, color: t.green, label: 'Paid' },
  no:  { bg: t.redLight,   color: t.red,   label: 'Unpaid' },
  na:  { bg: t.blueLight,  color: t.blue,  label: 'N/A' },
};
function FeesBadge({ status }) {
  const f = FEES[status] || FEES.na;
  return <span style={{ background: f.bg, color: f.color, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>{f.label}</span>;
}

const emptyForm = { full_name: '', email: '', date_of_birth: '', guardian_name: '', guardian_phone: '', fees_paid: 'no', fees_amount: '0', username: '', password: '' };
const calcAge = dob => { const b = new Date(dob); const n = new Date(); let a = n.getFullYear() - b.getFullYear(); if (n.getMonth() < b.getMonth() || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())) a--; return a; };

function Modal({ title, onClose, children }) {
  return (
    <div style={t.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...t.modal, maxWidth: '640px' }}>
        <div style={t.modalHeader}>
          <h3 style={t.modalTitle}>{title}</h3>
          <button style={t.closeBtn} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, children }) {
  return (
    <div style={t.fieldGroup}>
      <label style={t.label}>{label}</label>
      {children || <input style={t.input} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
}

export default function StudentsPage() {
  const isMobile = useIsMobile();
  const [students,     setStudents]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editStudent,  setEditStudent]  = useState(null);
  const [form,         setForm]         = useState(emptyForm);
  const [formError,    setFormError]    = useState('');
  const [saving,       setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchStudents = useCallback(async () => {
    try { const r = await axios.get('/api/students'); setStudents(r.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const openAdd = () => { setEditStudent(null); setForm(emptyForm); setFormError(''); setModalOpen(true); };
  const openEdit = s => {
    setEditStudent(s);
    setForm({ full_name: s.full_name, email: s.email, date_of_birth: s.date_of_birth,
      guardian_name: s.guardian_name || '', guardian_phone: s.guardian_phone || '',
      fees_paid: s.fees_paid, fees_amount: s.fees_amount,
      username: s.user?.username || '', password: '' });
    setFormError(''); setModalOpen(true);
  };
  const handleSave = async () => {
    setFormError('');
    if (!form.full_name || !form.email || !form.date_of_birth) return setFormError('Name, email and DOB are required.');
    if (!editStudent && (!form.username || !form.password)) return setFormError('Username and password are required.');
    setSaving(true);
    try {
      if (editStudent) await axios.put(`/api/students/${editStudent.id}`, form);
      else await axios.post('/api/students', form);
      setModalOpen(false); fetchStudents();
    } catch (e) { setFormError(e.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };
  const handleDelete = async () => {
    try { await axios.delete(`/api/students/${deleteTarget.id}`); setDeleteTarget(null); fetchStudents(); }
    catch (e) { console.error(e); }
  };

  const filtered = students.filter(s =>
    s.full_name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: 600, color: t.text, letterSpacing: '-0.3px', margin: 0 }}>Students</h2>
          <p style={{ fontSize: '13px', color: t.text3, margin: '4px 0 0' }}>{students.length} enrolled</p>
        </div>
        <button style={t.btnPrimary} onClick={openAdd}>+ Add Student</button>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <input style={{ ...t.input, width: '100%', maxWidth: '320px' }}
          placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: t.text3 }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...t.card, padding: '48px', textAlign: 'center', color: t.text3 }}>
          {search ? 'No results.' : 'No students yet.'}
        </div>
      ) : isMobile ? (
        /* ── Mobile: card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map(s => (
            <div key={s.id} style={{ ...t.card, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: t.accentLight,
                    border: `1px solid ${t.accentBorder}`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '14px', fontWeight: 600, color: t.accent }}>
                    {s.full_name[0]}
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: t.text }}>{s.full_name}</div>
                    <div style={{ fontSize: '12px', color: t.text3 }}>@{s.user?.username || '—'}</div>
                  </div>
                </div>
                <FeesBadge status={s.fees_paid}/>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                <div><span style={{ fontSize: '11px', color: t.text3 }}>Email </span><span style={{ fontSize: '12px', color: t.text2 }}>{s.email}</span></div>
                <div><span style={{ fontSize: '11px', color: t.text3 }}>Age </span><span style={{ fontSize: '12px', color: t.text2 }}>{s.date_of_birth ? calcAge(s.date_of_birth) + ' yrs' : '—'}</span></div>
                <div><span style={{ fontSize: '11px', color: t.text3 }}>Guardian </span><span style={{ fontSize: '12px', color: t.text2 }}>{s.guardian_name || '—'}</span></div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...t.btnSecondary, flex: 1, fontSize: '12px', padding: '7px' }} onClick={() => openEdit(s)}>Edit</button>
                <button style={{ ...t.btnDanger, flex: 1, fontSize: '12px', padding: '7px' }} onClick={() => setDeleteTarget(s)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Desktop: table ── */
        <div style={t.card}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>{['Student','Email','Age','Guardian','Fees','Username','Actions'].map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: t.accentLight,
                          border: `1px solid ${t.accentBorder}`, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: '13px', fontWeight: 600, color: t.accent, flexShrink: 0 }}>
                          {s.full_name[0]}
                        </div>
                        <span style={{ fontWeight: 500, color: t.text }}>{s.full_name}</span>
                      </div>
                    </td>
                    <td style={{ color: t.text2 }}>{s.email}</td>
                    <td style={{ color: t.text2 }}>{s.date_of_birth ? calcAge(s.date_of_birth) + ' yrs' : '—'}</td>
                    <td style={{ color: t.text2 }}>{s.guardian_name || '—'}</td>
                    <td><FeesBadge status={s.fees_paid}/></td>
                    <td style={{ color: t.text3, fontFamily: 'monospace', fontSize: '13px' }}>{s.user?.username || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button style={{ ...t.btnSecondary, padding: '5px 12px', fontSize: '12px' }} onClick={() => openEdit(s)}>Edit</button>
                        <button style={{ ...t.btnDanger, padding: '5px 12px', fontSize: '12px' }} onClick={() => setDeleteTarget(s)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal title={editStudent ? `Edit — ${editStudent.full_name}` : 'Add New Student'} onClose={() => setModalOpen(false)}>
          <div style={t.modalBody}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px,100%), 1fr))', gap: '14px' }}>
              <Field label="Full Name *" value={form.full_name} onChange={v => setForm(f => ({ ...f, full_name: v }))} placeholder="Arjun Sharma"/>
              <Field label="Email *" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="student@email.com"/>
              <div style={t.fieldGroup}>
                <label style={t.label}>Date of Birth *</label>
                <input style={t.input} type="date" value={form.date_of_birth} onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}/>
                {form.date_of_birth && <span style={{ fontSize: '12px', color: t.accent }}>Age: {calcAge(form.date_of_birth)}</span>}
              </div>
              <Field label="Guardian Name" value={form.guardian_name} onChange={v => setForm(f => ({ ...f, guardian_name: v }))} placeholder="Optional"/>
              <Field label="Guardian Phone" value={form.guardian_phone} onChange={v => setForm(f => ({ ...f, guardian_phone: v }))} placeholder="Optional"/>
              <div style={t.fieldGroup}>
                <label style={t.label}>Fees Status</label>
                <select style={t.select} value={form.fees_paid} onChange={e => setForm(f => ({ ...f, fees_paid: e.target.value }))}>
                  <option value="yes">Paid</option>
                  <option value="no">Unpaid</option>
                  <option value="na">N/A</option>
                </select>
              </div>
              <Field label="Fees Amount" type="number" value={form.fees_amount} onChange={v => setForm(f => ({ ...f, fees_amount: v }))} placeholder="0.00"/>
              {!editStudent && <>
                <Field label="Username *" value={form.username} onChange={v => setForm(f => ({ ...f, username: v }))} placeholder="student_login"/>
                <Field label="Password *" type="password" value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} placeholder="min. 6 characters"/>
              </>}
            </div>
            {formError && <div style={t.errorBox}>{formError}</div>}
          </div>
          <div style={t.modalFooter}>
            <button style={t.btnSecondary} onClick={() => setModalOpen(false)}>Cancel</button>
            <button style={t.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editStudent ? 'Save Changes' : 'Add Student'}
            </button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title="Delete Student" onClose={() => setDeleteTarget(null)}>
          <div style={{ ...t.modalBody, alignItems: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>⚠</div>
            <p style={{ color: t.text2 }}>Delete <strong style={{ color: t.text }}>{deleteTarget.full_name}</strong>? This cannot be undone.</p>
          </div>
          <div style={t.modalFooter}>
            <button style={t.btnSecondary} onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button style={t.btnDanger} onClick={handleDelete}>Yes, Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
